import React from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  Clock,
  MapPin,
  ShieldCheck,
  CheckCircle2,
  Users,
  ArrowRight,
  Sparkles,
  Video,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

/**
 * ============================================================
 * WORKSHOP STICKY CTA
 * ============================================================
 * High-conversion registration card for desktop sticky sidebar
 * and fixed bottom bar for mobile screens.
 *
 * Strictly enforces two-state public lifecycle:
 * - Upcoming -> "Register for this Workshop" (or "Registration Closed")
 * - Completed -> "Workshop Completed" + "Explore Session Highlights" (if available)
 * - Cancelled -> "Workshop Cancelled"
 */
export default function WorkshopStickyCTA({ workshop }) {
  if (!workshop) return null;

  const targetRegisterUrl = `/workshops/${workshop.slug}/register`;

  // Capacity calculation
  const capacity = Number(workshop.capacity) || 0;
  const registrationCount = Number(workshop.registration_count) || 0;
  const spotsRemaining =
    typeof workshop.spots_remaining === "number"
      ? workshop.spots_remaining
      : Math.max(0, capacity - registrationCount);

  const percentFilled =
    capacity > 0 ? Math.min(100, Math.round((registrationCount / capacity) * 100)) : 0;

  const isCompleted = Boolean(
    workshop.is_completed ||
    workshop.status === "Completed" ||
    workshop.lifecycle_stage === "completed_by_schedule"
  );

  const hasHighlights =
    Boolean(workshop.is_highlights_eligible) ||
    (isCompleted &&
      Boolean(workshop.show_highlights) &&
      Array.isArray(workshop.highlights) &&
      workshop.highlights.filter((h) => h && h.media_type === "highlight" && h.is_published !== false).length > 0);

  // Determine CTA Button configuration based strictly on public workshop lifecycle (Upcoming vs Completed)
  const getCtaConfig = () => {
    if (workshop.status === "Cancelled" || workshop.lifecycle_stage === "cancelled") {
      return {
        text: "Workshop Cancelled",
        disabled: true,
        actionType: "none",
        helper: "This session has been cancelled and is not accepting registrations.",
        badgeVariant: "cancelled",
        badgeText: "Cancelled",
      };
    }

    if (isCompleted) {
      return {
        text: "Workshop Completed",
        disabled: true,
        actionType: hasHighlights ? "highlights" : "none",
        actionLabel: "Explore Session Highlights",
        helper: hasHighlights
          ? "This session has concluded. Check out recorded moments and highlights below."
          : "This cohort session has concluded. Check back for future cohort dates.",
        badgeVariant: "completed",
        badgeText: "Completed",
      };
    }

    if (workshop.is_registration_open) {
      return {
        text: "Register for this Workshop",
        disabled: false,
        actionType: "register",
        helper: `Limited to ${capacity} seats. ${spotsRemaining} spots remaining.`,
        badgeVariant: "open",
        badgeText: "Open for Applications",
      };
    }

    return {
      text: "Registration Closed",
      disabled: true,
      actionType: "none",
      helper:
        spotsRemaining <= 0
          ? "All seats have been filled for this session."
          : "Registration is no longer accepting new applicants.",
      badgeVariant: "closed",
      badgeText: "Registration Closed",
    };
  };

  const cta = getCtaConfig();

  const handleScrollToHighlights = () => {
    const el = document.getElementById("session-highlights");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      {/* ============================================================ */}
      {/* 1. DESKTOP STICKY SIDEBAR CARD                               */}
      {/* ============================================================ */}
      <aside aria-label="Workshop Registration" className="w-full">
        <div className="rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_20px_60px_rgba(0,0,0,0.45)] overflow-hidden transition-all space-y-6">
          {/* Cover Image Preview */}
          <div className="relative aspect-[16/9] w-full overflow-hidden bg-black/60 border-b border-white/10">
            <img
              src={workshop.cover_image}
              alt={workshop.title}
              className="w-full h-full object-cover object-center"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&auto=format&fit=crop&q=80";
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" />

            {/* Mode & Category Chips on Cover */}
            <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between text-xs">
              <span className="px-2.5 py-0.5 rounded-full bg-black/70 border border-white/20 text-white font-medium backdrop-blur-md">
                {workshop.category}
              </span>
              <span className="px-2.5 py-0.5 rounded-full bg-primary/20 text-primary border border-primary/30 font-semibold backdrop-blur-md">
                {workshop.mode}
              </span>
            </div>
          </div>

          {/* Card Body */}
          <div className="p-6 pt-0 space-y-6">
            {/* Pricing Section */}
            <div className="flex items-baseline justify-between gap-3 border-b border-white/10 pb-5">
              <div>
                <span className="text-xs uppercase tracking-wider text-white/50 block font-medium mb-1">
                  Registration Fees
                </span>
                {workshop.is_free ? (
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-extrabold text-green-400 tracking-tight">
                      FREE
                    </span>
                    <span className="text-xs text-white/50 line-through">₹999</span>
                  </div>
                ) : (
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-extrabold text-white tracking-tight">
                      ₹{workshop.price?.toLocaleString("en-IN")}
                    </span>
                    <span className="text-xs text-white/50">INR (All inclusive)</span>
                  </div>
                )}
              </div>

              {/* Status Chip */}
              {cta.badgeVariant === "open" ? (
                <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-500/15 text-green-400 border border-green-500/30">
                  Open for Applications
                </span>
              ) : cta.badgeVariant === "completed" ? (
                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
                  Workshop Completed
                </span>
              ) : (
                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-white/10 text-white/60 border border-white/10">
                  {cta.badgeText || cta.text}
                </span>
              )}
            </div>

            {/* Primary CTA Button Section */}
            <div className="space-y-2.5 pt-1">
              {cta.actionType === "register" ? (
                <Link to={targetRegisterUrl} className="block w-full">
                  <Button
                    className={cn(
                      "w-full h-12 rounded-xl bg-primary text-black hover:bg-primary/90 font-bold text-sm tracking-wide gap-2",
                      "shadow-[0_0_22px_rgba(0,194,255,0.4)] hover:shadow-[0_0_28px_rgba(0,194,255,0.65)] transition-all duration-300"
                    )}
                  >
                    <span>{cta.text}</span>
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              ) : cta.actionType === "highlights" ? (
                <div className="space-y-2">
                  <Button
                    disabled
                    className="w-full h-10 rounded-xl bg-white/10 text-white/50 cursor-not-allowed font-medium text-xs tracking-wide"
                  >
                    {cta.text}
                  </Button>
                  <Button
                    onClick={handleScrollToHighlights}
                    className="w-full h-12 rounded-xl bg-cyan-500 text-black hover:bg-cyan-400 font-bold text-xs tracking-wide gap-2 shadow-[0_0_22px_rgba(6,182,212,0.4)] transition-all"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>{cta.actionLabel}</span>
                  </Button>
                </div>
              ) : (
                <Button
                  disabled
                  className="w-full h-12 rounded-xl bg-white/10 text-white/50 cursor-not-allowed font-bold text-sm tracking-wide"
                >
                  {cta.text}
                </Button>
              )}

              <p className="text-[11px] text-white/50 text-center leading-relaxed">
                {cta.helper}
              </p>
            </div>

            {/* Availability / Seat Meter */}
            {workshop.is_registration_open && (
              <div className="space-y-2 p-3.5 rounded-2xl border border-white/5 bg-white/[0.02]">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-white/60 flex items-center gap-1.5 font-medium">
                    <Users className="w-3.5 h-3.5 text-primary" />
                    <span>Cohort Capacity</span>
                  </span>
                  <span className="text-white font-semibold">
                    {spotsRemaining} {spotsRemaining === 1 ? "seat" : "seats"} left
                  </span>
                </div>
                <Progress value={percentFilled} className="h-1.5 bg-white/10" />
                <div className="text-[11px] text-white/45 flex justify-between">
                  <span>{registrationCount} enrolled</span>
                  <span>Max {capacity} students</span>
                </div>
              </div>
            )}

            {/* Quick Logistics Summary */}
            <div className="space-y-2.5 text-xs text-white/70">
              <div className="flex items-center justify-between py-1 border-b border-white/5">
                <span className="flex items-center gap-2 text-white/50">
                  <Calendar className="w-3.5 h-3.5 text-primary" />
                  <span>Session Date</span>
                </span>
                <span className="font-semibold text-white">{workshop.date}</span>
              </div>
              <div className="flex items-center justify-between py-1 border-b border-white/5">
                <span className="flex items-center gap-2 text-white/50">
                  <Clock className="w-3.5 h-3.5 text-primary" />
                  <span>Time</span>
                </span>
                <span className="font-semibold text-white">
                  {workshop.start_time} - {workshop.end_time}
                </span>
              </div>
              <div className="flex items-center justify-between py-1 border-b border-white/5">
                <span className="flex items-center gap-2 text-white/50">
                  <Clock className="w-3.5 h-3.5 text-primary" />
                  <span>Total Duration</span>
                </span>
                <span className="font-semibold text-white">{workshop.duration}</span>
              </div>
              <div className="flex items-center justify-between py-1">
                <span className="flex items-center gap-2 text-white/50">
                  {workshop.mode === "Online" ? (
                    <Video className="w-3.5 h-3.5 text-primary" />
                  ) : (
                    <MapPin className="w-3.5 h-3.5 text-primary" />
                  )}
                  <span>Mode</span>
                </span>
                <span className="font-semibold text-white truncate max-w-[150px]" title={workshop.mode}>
                  {workshop.mode}
                </span>
              </div>
            </div>

            {/* What's Included Value Checklist */}
            <div className="pt-5 border-t border-white/10 space-y-3">
              <span className="text-xs uppercase tracking-wider text-white/50 font-semibold block">
                This Workshop Includes
              </span>
              <ul className="space-y-2 text-xs text-white/75">
                {workshop.certificate_available && (
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    <span>Verified Shiveda Certificate of Completion</span>
                  </li>
                )}
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                  <span>Live Interactive Architecture Q&A</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                  <span>Full Production GitHub Repository & Code</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                  <span>Access to Shiveda Alumni Engineering Network</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </aside>

      {/* ============================================================ */}
      {/* 2. MOBILE FIXED BOTTOM CTA BAR (Viewport < md)               */}
      {/* ============================================================ */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-black/90 backdrop-blur-xl border-t border-white/15 p-3.5 px-5 shadow-[0_-10px_30px_rgba(0,0,0,0.6)]">
        <div className="flex items-center justify-between gap-4 max-w-lg mx-auto">
          {/* Mobile Price & Seats */}
          <div>
            <div className="text-[11px] text-white/50 font-medium uppercase">Tuition</div>
            {workshop.is_free ? (
              <div className="flex items-baseline gap-1.5">
                <span className="text-xl font-extrabold text-green-400">FREE</span>
                <span className="text-[10px] text-white/50 line-through">₹999</span>
              </div>
            ) : (
              <div className="text-xl font-extrabold text-white">
                ₹{workshop.price?.toLocaleString("en-IN")}
              </div>
            )}
            {workshop.is_registration_open && (
              <div className="text-[10px] text-white/60">
                {spotsRemaining} seats left
              </div>
            )}
          </div>

          {/* Mobile Action Button */}
          <div className="flex-1 max-w-[200px]">
            {cta.actionType === "register" ? (
              <Link to={targetRegisterUrl} className="block w-full">
                <Button
                  size="sm"
                  className="w-full h-10 rounded-xl bg-primary text-black hover:bg-primary/90 text-xs font-bold shadow-[0_0_18px_rgba(0,194,255,0.4)]"
                >
                  <span>Register</span>
                  <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </Link>
            ) : cta.actionType === "highlights" ? (
              <Button
                onClick={handleScrollToHighlights}
                size="sm"
                className="w-full h-10 rounded-xl bg-cyan-500 text-black hover:bg-cyan-400 font-bold text-xs gap-1.5 shadow-[0_0_18px_rgba(6,182,212,0.4)]"
              >
                <Sparkles className="w-3 h-3" />
                <span>Highlights</span>
              </Button>
            ) : (
              <Button
                disabled
                size="sm"
                className="w-full h-10 rounded-xl bg-white/10 text-white/50 text-xs font-bold"
              >
                {cta.text}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
